import type { BinStatus, StorageBin } from '../types';

/**
 * Where a bin stands against its band, worst first. The server computes this for the list;
 * the site view derives it here from the same fields, by the same rule, so a bin reads the same
 * in both places.
 */
export function binStatusOf(
  holdsMaterial: boolean,
  quantity: number,
  minQuantity: number | null,
  maxQuantity: number | null
): BinStatus {
  if (!holdsMaterial) return 'empty';
  if (quantity < 0) return 'negative';
  if (minQuantity !== null && quantity < minQuantity) return 'below-min';
  if (maxQuantity !== null && quantity > maxQuantity) return 'over-max';
  return 'ok';
}

export function storageBinStatus(bin: Pick<StorageBin, 'material' | 'quantity' | 'minQuantity' | 'maxQuantity'>): BinStatus {
  return binStatusOf(bin.material !== null, bin.quantity, bin.minQuantity, bin.maxQuantity);
}

export const BIN_STATUS_LABEL: Record<BinStatus, string> = {
  negative: 'Below zero',
  'below-min': 'Below min',
  'over-max': 'Over max',
  ok: 'OK',
  empty: 'Empty',
};

export const BIN_STATUS_VARIANT: Record<BinStatus, 'danger' | 'warning' | 'success' | 'secondary'> = {
  negative: 'danger',
  'below-min': 'danger',
  'over-max': 'warning',
  ok: 'success',
  empty: 'secondary',
};

/** What the status asks someone to do, for a tooltip. */
export const BIN_STATUS_HINT: Record<BinStatus, string> = {
  negative: 'The ledger shows less than nothing: a draw was recorded that the stock did not cover. Count it.',
  'below-min': 'Below its minimum: reorder or transfer some in.',
  'over-max': 'Above its maximum: do not send more here.',
  ok: 'Within its band.',
  empty: 'No material assigned to this bin.',
};

export function needsAction(status: BinStatus): boolean {
  return status === 'negative' || status === 'below-min' || status === 'over-max';
}
